import { Fragment, useEffect, useState } from "react";
import "./accountmanagementpage.css";
import { Page } from "../page";
import { AccountManagementPageBody } from "./accountmanagementpagebody";
import { useDispatch, useSelector } from "react-redux";
import {
  deepCompare,
  isValidEmail,
  notificationDurationMiddle,
} from "../../lib/utils";
import {
  setCurrentPage,
  setCurrentPageIsDirty,
} from "../../redux/slices/currentpageslice";
import { BLOCK_DIRTY_MESSAGE } from "../../settings";
import {
  cancelSubscription,
  disposePage,
  loadPage,
  resizeSubscription,
  setFormValue,
  setPageValue,
  setProcessing,
  setSelectedTab,
  setTabPosition,
  setUserFormValue,
  updateUser,
} from "../../redux/slices/accountmanagementslice";
import { Dialog } from "@progress/kendo-react-dialogs";
import { ChangePasswordForm } from "../../components/changepasswordform";
import { addNotification } from "../../redux/slices/notificationsslice";
import { apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import { useNavigate } from "react-router-dom";
import { useEffectOnce } from "../../hooks/useeffectonce";
import { hideAppDialog, setAppDialog } from "../../redux/slices/appdialogslice";

import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const AccountManagementPage = (props) => {
  const dispatch = useDispatch();
  const currentPageState = useSelector((state) => state.currentPage);
  const pageState = useSelector((state) => state.accountManagementPage);
  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const appearance = useSelector((state) => state.appearance);
  const navigate = useNavigate();

  const { t } = useTranslation();

  let currentPageId = "accountManagementPage";

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  /*block navigation if dirty*/
  const updateDirtyState = () => {
    let isDirty = !deepCompare(
      pageState.formInitialValues,
      pageState.formValues
    );
    let wasDirty = currentPageState.isDirty;

    if (isDirty != wasDirty) dispatch(setCurrentPageIsDirty(isDirty));
  };

  const blockNavigationIfDirty = (msg) => {
    if (currentPageState.isDirty) {
      if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true) {
        dispatch(setCurrentPageIsDirty(false));
        return false;
      } else return true;
    } else return false;
  };
  /*block navigation if dirty*/

  /*initial page load*/
  useEffectOnce(() => {
    dispatch(loadPage(t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("accountmang.accountmang.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("accountmang.accountmang.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );

    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  const handleOnChangeUserField = (id, value) => {
    dispatch(
      setUserFormValue({
        id: id,
        value: value,
      })
    );
  };

  const handleOnUpdateUserInfo = () => {
    var name = pageState.formValues.User.Name;
    var desc = pageState.formValues.User.Description;
    if (!name) {
      dispatch(
        addNotification({
          text: t("accountmang.accountmang.fillname"),
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        })
      );
      return;
    }
    dispatch(updateUser(name, desc, t)).then(
      (response) => {
        dispatch(
          addNotification({
            text: t("accountmang.accountmang.userupdate"),
            type: "success",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
      },
      (err) => {
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("accountmang.accountmang.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
          }

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("accountmang.accountmang.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  const handleOnChangePasswordClick = () => {
    setChangePasswordOpen(true);
  };

  const handleOnCancelSubscription = () => {
    let email = currentUser.Email;
    dispatch(
      setAppDialog({
        isVisible: true,
        className: "accountmanagement-page-are-you-sure",
        title: t("accountmang.accountmang.warning"),
        content: t("accountmang.accountmang.areyousure"),
        actions: {
          yesButton: {
            title: t("accountmang.accountmang.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(cancelSubscription(email, t)).then(
                (response) => {},
                (err) => {
                  dispatch(setProcessing({ processing: false }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [
                        t("accountmang.accountmang.unknown_error"),
                      ];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessage = t(
                      "accountmang.accountmang.unknown_error"
                    );
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                }
              );
            },
          },
          noButton: { title: t("accountmang.accountmang.no") },
        },
      })
    );
  };

  const handleOnCloseAccountManagement = () => {
    navigate("/");
  };

  const handleOnStoreClick = () => {
    window.open("https://www.nopcommerce.com", "_blank");
  };

  const handleOnResizeClick = (plan) => {
    dispatch(
      setAppDialog({
        isVisible: true,
        className: "accountmanagement-page-are-you-sure",
        title: t("accountmang.accountmang.warning"),
        content: t("accountmang.accountmang.areyousure1"),
        actions: {
          yesButton: {
            title: t("accountmang.accountmang.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(resizeSubscription(plan.Id, t)).then(
                (response) => {
                  if (response.data.Data.CanChange) {
                    if (plan.PaymentLink) {
                      window.open(plan.PaymentLink);
                    } else {
                      dispatch(
                        addNotification({
                          text: t("accountmang.accountmang.nopayment"),
                          type: "warning",
                          closable: true,
                          duration: notificationDurationMiddle,
                          position: "topRight",
                        })
                      );
                    }
                  } else {
                    dispatch(
                      addNotification({
                        text: t("accountmang.accountmang.nochange"),
                        type: "warning",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  }
                },
                (err) => {
                  dispatch(setProcessing({ processing: false }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [
                        t("accountmang.accountmang.unknown_error"),
                      ];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessage = t(
                      "accountmang.accountmang.unknown_error"
                    );
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                }
              );
            },
          },
          noButton: { title: t("accountmang.accountmang.no") },
        },
      })
    );
  };

  const handleOnTabSelection = (e) => {
    dispatch(setSelectedTab(e.selected));
  };
  const handleOnChangeTabPosition = (e) => {
    dispatch(setPageValue({ id: "tabPosition", value: e.value }));
  };

  useEffect(() => {
    dispatch(setPageValue({ id: "tabPosition", value: "top" }));
  }, [t]);

  return (
    <Fragment>
      <Page
        id="accountManagementPage"
        className="accountmanagement-page"
        title={t("accountmang.accountmang.account")}
        animation={props.animation}
      >
        <AccountManagementPageBody
          updateDirtyState={updateDirtyState}
          handleOnChangeField={handleOnChangeUserField}
          onUpdateUserInfo={handleOnUpdateUserInfo}
          onChangePasswordClick={handleOnChangePasswordClick}
          onCancelSubsciption={handleOnCancelSubscription}
          onCloseAccountManagement={handleOnCloseAccountManagement}
          onStoreClick={handleOnStoreClick}
          onResizeClick={handleOnResizeClick}
          onTabSelected={handleOnTabSelection}
          onChangeTabPosition={handleOnChangeTabPosition}
        />
      </Page>

      {changePasswordOpen && (
        <Dialog className="change-password-form-dialog" title={null}>
          <ChangePasswordForm
            currentTheme={currentTheme}
            onCancel={(e) => setChangePasswordOpen(false)}
          />
        </Dialog>
      )}

      <style>
        {`
          .page.accountmanagement-page > .content { 
            overflow:auto;
          }
          .page.accountmanagement-page .k-animation-container.k-animation-container-relative{
            width:100%;direction:${appearance.isRightToLeft ? "rtl" : "ltr"};
          }

          .page.accountmanagement-page .content .btn-primary ,
          .page.accountmanagement-page .content .btn-primary.disabled,
          .page.accountmanagement-page .content .btn-primary:disabled ,
          .page.accountmanagement-page .content .btn-primary.disabled:hover, 
          .page.accountmanagement-page .content .btn-primary.disabled:focus,
          .page.accountmanagement-page .content .btn-primary:disabled:hover, 
          .page.accountmanagement-page .content .btn-primary:disabled:focus
          {
            opacity: 1;
            color: #ffffff;
            background-color:${currentTheme.touchColor} ;
          }
        `}
      </style>
    </Fragment>
  );
};
